import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Drawer,
  DrawerClose,
  Tabs,
  Button,
  Icon,
  StatusPill,
  ProgressBar,
  LineAreaChart,
  DonutChart,
  DataTable,
  Pagination,
  chart,
} from '../../campaign-os';
import { useShellToast } from '../useShellToast';
import { useApiData } from '../data/useApiData';
import { mapCampaign } from '../data/mapCampaign';
import { messagePill } from '../data/messageStatus';
import { CHANNEL_COLOR, CHANNEL_NAME, totalReach } from '../data/channelChart';
import { ChannelReach } from '../components/ChannelReach';
import { ResendDialog } from '../components/ResendDialog';
import { CampaignAdvanced } from '../components/CampaignAdvanced';
import { campaignService } from '../../../../services/apiService';
import { formatDate, formatShortDate } from '../data/format';

/**
 * Campaign details drawer.
 *
 * - Real campaigns (mapped API rows): GET /campaigns/:id for counts / 30-day daily series /
 *   status breakdown / recent events, GET /campaigns/:id/messages for the Recipients and Failed
 *   tabs, POST /campaigns/:id/retry-failed as the single "Retry failed sends" action.
 * - Delivery data arrives from the provider's event webhook, so the drawer simply re-reads
 *   itself every minute while the campaign is < 24h old; GET /campaigns/sync-status says
 *   whether that webhook is configured at all.
 */
const AUTO_REFRESH_MS = 60_000;
const FRESH_HOURS = 24;

const hoursSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 3_600_000 : Infinity);
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null);
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'recipients', label: 'Recipients' },
  { key: 'analytics', label: 'Analytics' },
  // Not "Failed": that name is taken by the KPI that counts only our own send failures,
  // while this tab also lists every bounce. Reading "Failed 0" above "Failed 104" is wrong.
  { key: 'failed', label: 'Failed to deliver' },
];
const PAGE_SIZE = 8;
/** Recipients-tab filters. `null` = every address field. */
const KIND_FILTERS = [
  { label: 'Everyone', value: null },
  { label: 'To', value: 'TO' },
  { label: 'CC', value: 'CC' },
  { label: 'BCC', value: 'BCC' },
];
/** cos-status-coral (tailwind.config.cjs) — chartTheme has no coral token. */
const CORAL = '#A35A4E';
const BREAKDOWN_COLOR = {
  Clicked: chart.tealDeep,
  Opened: chart.teal,
  Delivered: chart.ramp[0],
  'Sent (no event)': chart.blueTeal,
  Bounced: chart.bronze,
  Unsubscribed: chart.palette[3],
  Failed: CORAL,
  Queued: chart.status.Draft,
};
const BATCH_LINK_CLASS =
  'inline-flex h-9 items-center gap-2 rounded-xl border border-cos-line bg-white/65 px-3 text-[13px] font-semibold text-cos-ink transition-colors hover:border-cos-teal/35 hover:text-cos-teal-deep';

const num = (n) => Number(n || 0).toLocaleString('en-US');
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN') : '—');
/**
 * Compact timestamp for table cells — "9 Sep, 3:16 pm" rather than the locale's
 * "9/9/2026, 3:16:59 pm". The long form was wide enough on its own to push the recipient
 * table into a horizontal scroll.
 */
const stamp = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : '—';

function Metric({ label, value, tone = 'ink', hint = null }) {
  const color = tone === 'teal' ? 'text-cos-teal-deep' : tone === 'coral' ? 'text-cos-status-coral' : 'text-cos-ink';
  return (
    <div className="glass-inset px-3 py-2.5">
      <div className="text-2xs font-semibold uppercase tracking-wide text-cos-ink-muted">{label}</div>
      <div className={`mono mt-0.5 text-lg font-bold leading-none ${color}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] leading-[14px] text-cos-ink-muted">{hint}</div>}
    </div>
  );
}

function InfoRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-cos-line-subtle py-2.5 last:border-0">
      <span className="text-[13px] text-cos-ink-muted">{label}</span>
      <span className="text-right text-[13px] font-medium text-cos-ink">{children}</span>
    </div>
  );
}

function Pulse({ rows = 4 }) {
  return (
    <div className="animate-pulse space-y-2.5 py-2">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-9 rounded-lg bg-[rgba(70,80,72,0.08)]" />
      ))}
    </div>
  );
}

function EmptyBlock({ icon, children }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-cos-ink-muted">
      <Icon name={icon} className="text-[30px] text-cos-teal" />
      <span className="text-sm">{children}</span>
    </div>
  );
}

/**
 * Which address field a copy rode in on. To / CC / BCC read as jargon on their own, so
 * the chip carries the plain-language meaning as its tooltip rather than a legend
 * nobody reads.
 */
const CHANNEL_TITLE = {
  TO: 'Addressed directly in the To line',
  CC: 'Copied — visible to everyone else on the mail',
  BCC: 'Blind-copied — invisible to the other recipients, and to each other',
};

function ChannelChip({ kind }) {
  if (!kind) return null;
  // Tinted from the one palette the whole section uses, so the chip beside a recipient is
  // the same colour as that field's ring and its row in the table. Hex + alpha rather
  // than Tailwind classes because the palette is data, shared with the charts.
  const color = CHANNEL_COLOR[kind] || CHANNEL_COLOR.TO;
  return (
    <span
      title={CHANNEL_TITLE[kind] || ''}
      style={{ color, borderColor: `${color}55`, background: `${color}14` }}
      className="inline-flex shrink-0 items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em]"
    >
      {CHANNEL_NAME[kind] || kind}
    </span>
  );
}

export function CampaignDetail({ campaign, onClose }) {
  if (!campaign) return null;
  // Keyed by id so tab / page / fetch state reset when a different campaign is opened.
  return <LiveCampaignDrawer key={campaign.id} campaign={campaign} onClose={onClose} />;
}

/* ------------------------------------------------------------------------------------------ */
/* Real campaigns — live detail from the backend.                                              */
/* ------------------------------------------------------------------------------------------ */
function LiveCampaignDrawer({ campaign, onClose }) {
  const toast = useShellToast();
  const [tab, setTab] = useState('overview');
  const [page, setPage] = useState(1);
  // Which address field the Recipients tab is showing; null = all of them.
  const [kind, setKind] = useState(null);
  const [resendOpen, setResendOpen] = useState(false);
  const rawId = campaign.rawId;

  const detail = useApiData(() => campaignService.get(rawId), [rawId], { cacheKey: `campaign:detail:${rawId}` });

  // Recipients → all messages; Failed to deliver → the server's "failed" alias (FAILED + bounces + spam + blocked).
  const listTab = tab === 'recipients';
  const tabStatus = tab === 'failed' ? 'failed' : undefined;
  // The Failed-to-deliver tab is about sends that went wrong, whichever field carried them.
  const listKind = tab === 'recipients' ? kind : null;
  const msgs = useApiData(
    () =>
      campaignService
        .messages(rawId, { status: tabStatus, kind: listKind ?? undefined, page, limit: PAGE_SIZE })
        // Tag the response with the filter it answers, so a tab or channel switch never
        // shows the previous filter's rows.
        .then((res) => ({ ...res, forStatus: `${tabStatus ?? 'all'}:${listKind ?? 'all'}` })),
    [rawId, tabStatus, listKind, page],
    { enabled: listTab }
  );

  const detailRow = detail.data?.data || null;
  // Prefer the fresh detail row (it updates after a retry); fall back to the list row.
  const live = useMemo(() => (detailRow ? mapCampaign(detailRow) : campaign), [detailRow, campaign]);
  const counts = live.counts;
  const rates = live.rates || {};
  const breakdown = detailRow?.status_breakdown || null;
  // What the Failed-to-deliver tab lists: our own send failures AND every kind of bounce. The
  // badge must count the same rows the list shows, or it reads "0" above seventeen entries.
  // A Mailtrap block is not one of them: it counts as sent (operator decision, 23 Oct 2026).
  const failedTabCount = breakdown
    ? ['FAILED', 'SOFT_BOUNCE', 'HARD_BOUNCE', 'SPAM'].reduce((n, k) => n + Number(breakdown[k] || 0), 0)
    : counts.failed_count + counts.bounced_count;
  const queued = breakdown
    ? Number(breakdown.QUEUED || 0)
    : Math.max(0, counts.total_count - counts.sent_count - counts.failed_count);
  const hasBatch = live.regularizationBatchId !== null && live.regularizationBatchId !== undefined;
  const batchHref = hasBatch ? `/vendor-regularization/batch/${live.regularizationBatchId}` : null;
  // A finished broadcast can be sent again to the vendors it missed; the dialog works out who.
  const canResend =
    live.type === 'BROADCAST' && ['SENT', 'FAILED'].includes(detailRow?.status) && Number(counts.total_count || 0) > 0;

  const daily = useMemo(
    () => (detailRow?.daily || []).map((d) => ({ ...d, label: formatShortDate(d.date) })),
    [detailRow]
  );

  const donut = useMemo(() => {
    if (!breakdown) return [];
    const b = (k) => Number(breakdown[k] || 0);
    return [
      { name: 'Clicked', value: b('CLICKED') },
      { name: 'Opened', value: b('OPENED') },
      { name: 'Delivered', value: b('DELIVERED') },
      { name: 'Sent (no event)', value: b('SENT') + b('BLOCKED') },
      { name: 'Bounced', value: b('SOFT_BOUNCE') + b('HARD_BOUNCE') + b('SPAM') },
      { name: 'Unsubscribed', value: b('UNSUBSCRIBED') },
      { name: 'Failed', value: b('FAILED') },
      { name: 'Queued', value: b('QUEUED') },
    ].filter((s) => s.value > 0);
  }, [breakdown]);

  const events = (detailRow?.recent_events || []).slice(0, 8);
  const channels = detailRow?.channels || [];
  // Only a shared send (one To, everyone else copied) has copies to break out; an ordinary
  // per-vendor campaign gets the same reach card with its one field, To.
  const hasCopies = channels.some((c) => c.kind !== 'TO');
  // Every address the mail reached, across all the fields it used.
  const reach = totalReach(channels);

  const msgsFresh = !!msgs.data && msgs.data.forStatus === `${tabStatus ?? 'all'}:${listKind ?? 'all'}`;
  const msgRows = msgsFresh ? msgs.data.data || [] : [];
  const msgMeta = (msgsFresh && msgs.data.meta) || { page, limit: PAGE_SIZE, total: msgRows.length };

  const changeTab = (key) => {
    setTab(key);
    setPage(1);
  };

  const refreshAll = () => {
    detail.refetch();
    msgs.refetch();
  };


  // ---- Keep delivery data fresh without anyone pressing a button ----------------------------
  const syncStatus = useApiData(() => campaignService.syncStatus(), [], { cacheKey: 'campaign:sync-status' });
  const tracking = syncStatus.data?.data || null;
  // Delivery, open and click facts arrive ONLY on the provider's webhook. With no webhook
  // configured a campaign can still be sent, but its numbers will never move past Sent —
  // which is worth saying outright rather than leaving someone to wonder.
  const trackingLive = tracking ? tracking.webhook_configured !== false : true;
  // The API row keeps its counters under `counts` (see mapCampaign); `live.counts` is that.
  const rawStatus = detailRow?.status;
  const wentOut = (rawStatus === 'SENT' || rawStatus === 'SENDING') && Number(counts.sent_count || 0) > 0;
  const ageHours = hoursSince(detailRow?.created_at);

  // While a campaign is fresh, re-read every minute so newly arrived events show up here.
  const isFresh = wentOut && ageHours < FRESH_HOURS;
  useEffect(() => {
    if (!isFresh) return undefined;
    const timer = setInterval(() => {
      detail.refetch();
      if (listTab) msgs.refetch();
      syncStatus.refetch();
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [isFresh, listTab, detail.refetch, msgs.refetch, syncStatus.refetch]);

  const footerNote = (() => {
    if (!trackingLive) return 'Delivery tracking is not configured — this campaign will not report opens';
    return `Delivery and open data arrive as they happen · updated ${live.updated ? formatDate(live.updated) : '—'}`;
  })();

  /**
   * Four columns, and every one of them shrinkable — the table must never scroll
   * sideways inside the drawer. The address rides under the name instead of claiming a
   * column of its own (that fifth column was what overflowed), `max-w-0` lets the
   * flexible column actually shrink so `truncate` has something to bite on, and the
   * fixed columns are sized to their content.
   */
  const recipientColumns = [
    {
      key: 'recipient',
      header: 'Recipient',
      className: 'max-w-0',
      render: (m) => (
        <div className="min-w-0">
          <div className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate font-medium text-cos-ink">
              {m.vendor?.company_name || (
                // Copy lists collect people never onboarded as vendors — an internal
                // stakeholder, a shared inbox. Saying so beats echoing the address below.
                <span className="italic text-cos-ink-muted">Not a registered vendor</span>
              )}
            </span>
            {m.vendor?.vendor_code && (
              <span className="mono shrink-0 text-[10px] text-cos-ink-muted">{m.vendor.vendor_code}</span>
            )}
          </div>
          <div className="mono truncate text-2xs text-cos-ink-secondary">{m.recipient}</div>
        </div>
      ),
    },
    ...(hasCopies
      ? [{ key: 'kind', header: 'Field', width: 68, render: (m) => <ChannelChip kind={m.kind} /> }]
      : []),
    {
      key: 'status',
      header: 'Status',
      width: 104,
      render: (m) => {
        const p = messagePill(m.status);
        return <StatusPill status={p.pill} label={p.label} />;
      },
    },
    {
      key: 'last_event_at',
      header: 'Last event',
      align: 'right',
      width: 112,
      render: (m) => (
        <span className="mono whitespace-nowrap text-2xs text-cos-ink-secondary">
          {stamp(m.last_event_at || m.sent_at)}
        </span>
      ),
    },
  ];

  const messagesPager = (
    <div className="border-t border-cos-line-subtle px-3 py-2.5">
      <Pagination page={msgMeta.page} pageSize={msgMeta.limit} total={msgMeta.total} onPageChange={setPage} noun="recipients" />
    </div>
  );

  return (
    <Drawer open onClose={onClose} width={tab === 'failed' ? 'max-w-[min(1240px,96vw)]' : 'max-w-2xl'}>
      {/* Header */}
      <div className="border-b border-cos-line-subtle px-6 pb-4 pt-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-1.5 flex items-center gap-2">
              <span title={live.apiStatusLabel}>
                <StatusPill status={live.status} />
              </span>
              <span className="mono text-2xs text-cos-ink-muted">{live.id}</span>
            </div>
            <h2 className="text-xl font-bold leading-tight tracking-tight text-cos-ink">{live.name}</h2>
            <p className="mt-1 text-[13px] text-cos-ink-secondary">
              {live.templateName} · Email · Owner {live.owner}
            </p>
          </div>
          <DrawerClose onClose={onClose} />
        </div>

        {(hasBatch || canResend) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {hasBatch && (
              <Link to={batchHref} className={BATCH_LINK_CLASS}>
                <Icon name="open_in_new" className="-ml-0.5 text-[17px]" weight={500} /> Open batch
              </Link>
            )}
            {canResend && failedTabCount > 0 && (
              <Button variant="primary" size="sm" icon="refresh" onClick={() => setResendOpen(true)}>
                Retry failed sends
              </Button>
            )}
          </div>
        )}
        <ResendDialog
          open={resendOpen}
          campaignId={rawId}
          onClose={() => setResendOpen(false)}
          onSent={(res) => {
            setResendOpen(false);
            toast(res?.message || 'Resend queued', { tone: 'success', icon: 'forward_to_inbox' });
            refreshAll();
          }}
        />
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {detail.error && !detailRow && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-cos-status-coral/30 bg-cos-status-coral-bg px-3 py-2.5 text-[12px] text-cos-status-coral">
            <span className="flex min-w-0 items-center gap-2">
              <Icon name="error" className="text-[16px]" weight={500} />
              <span className="truncate">Couldn&apos;t load live detail — {detail.error}</span>
            </span>
            <Button variant="ghost" size="sm" icon="refresh" onClick={detail.refetch}>
              Retry
            </Button>
          </div>
        )}

        <div className="mb-5 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          <Metric label="Recipients" value={num(counts.total_count)} hint="addresses on the list" />
          <Metric label="Sent" value={num(counts.sent_count)} hint="handed to Mailtrap" />
          <Metric label="Delivered" value={num(counts.delivered_count)} hint="reached the inbox" />
          <Metric label="Opened" value={num(counts.opened_count)} tone="teal" hint="opened at least once" />
          <Metric label="Clicked" value={num(counts.clicked_count)} tone="teal" hint="clicked a link" />
          <Metric label="Bounced" value={num(counts.bounced_count)} tone={counts.bounced_count > 0 ? 'coral' : 'ink'} hint="their mail server refused it" />
          <Metric label="Failed" value={num(counts.failed_count)} tone={counts.failed_count > 0 ? 'coral' : 'ink'} hint="never handed to Mailtrap" />
        </div>

        <Tabs
          items={[
            TABS[0],
            { ...TABS[1], count: counts.total_count },
            TABS[2],
            { ...TABS[3], count: failedTabCount },
          ]}
          value={tab}
          onChange={changeTab}
          size="sm"
          className="mb-4"
        />

        {/* Overview */}
        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="glass-inset p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[13px] font-semibold text-cos-ink">Completion</span>
                <span className="mono text-[13px] font-bold text-cos-teal-deep">{live.progress}%</span>
              </div>
              <ProgressBar value={live.progress} />
              <div className="mt-3 flex justify-between text-2xs text-cos-ink-muted">
                <span>{num(counts.sent_count)} sent</span>
                <span>{num(queued)} queued</span>
                <span>{num(counts.failed_count)} failed</span>
              </div>
            </div>

            {live.regularization && (
              <div className="glass-inset p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[13px] font-semibold text-cos-ink">Vendor responses (regularization form)</span>
                  <span className="mono text-[13px] font-bold text-cos-teal-deep">{live.regularization.response_rate ?? 0}%</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    ['Sent', live.regularization.requests?.sent],
                    ['Submitted', live.regularization.requests?.submitted],
                    ['Verified', live.regularization.requests?.verified],
                    ['Expired', live.regularization.requests?.expired],
                  ].map(([label, value]) => (
                    <div key={label} className="text-center">
                      <div className="mono text-base font-bold text-cos-ink">{num(value)}</div>
                      <div className="text-2xs font-semibold uppercase tracking-wide text-cos-ink-muted">{label}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-2xs text-cos-ink-muted">
                  Batch {live.regularization.batch_status || '—'} · sent {when(live.regularization.sent_at)}
                  {live.regularization.completed_at ? ` · completed ${when(live.regularization.completed_at)}` : ''}
                </div>
              </div>
            )}

            <div>
              <InfoRow label="Type">{live.templateName}</InfoRow>
              <InfoRow label="Subject">{live.subject || '—'}</InfoRow>
              <InfoRow label="Status">{live.apiStatusLabel}</InfoRow>
              {hasBatch && (
                <InfoRow label="Regularization batch">
                  <span className="mono">#{live.regularizationBatchId}</span>
                  <Link to={batchHref} className="ml-2 text-cos-teal-deep hover:underline">
                    Open batch
                  </Link>
                </InfoRow>
              )}
              <InfoRow label="Owner">{live.owner}</InfoRow>
              <InfoRow label="Created">{live.created ? formatDate(live.created) : '—'}</InfoRow>
              <InfoRow label="Link expiry">{live.deadline ? formatDate(live.deadline) : '—'}</InfoRow>
              <InfoRow label="Delivery rate"><span className="mono">{rates.delivery ?? 0}%</span></InfoRow>
              <InfoRow label="Open rate"><span className="mono">{rates.open ?? 0}%</span></InfoRow>
              <InfoRow label="Click rate"><span className="mono">{rates.click ?? 0}%</span></InfoRow>
            </div>
          </div>
        )}

        {/* Recipients */}
        {tab === 'recipients' && (
          <div className="overflow-hidden rounded-xl border border-cos-line-subtle">
            {hasCopies && (
              <div className="flex flex-wrap items-center gap-1.5 border-b border-cos-line-subtle bg-white/40 px-3 py-2">
                <span className="mr-0.5 text-2xs font-semibold text-cos-ink-muted">Show</span>
                {KIND_FILTERS.map((f) => {
                  const active = (kind ?? null) === f.value;
                  return (
                    <button
                      key={f.label}
                      type="button"
                      onClick={() => {
                        setKind(f.value);
                        setPage(1);
                      }}
                      className={`rounded-lg border px-2 py-1 text-2xs font-semibold transition-colors ${
                        active
                          ? 'border-cos-teal/40 bg-cos-teal-faint/60 text-cos-teal-deep'
                          : 'border-cos-line bg-white/60 text-cos-ink-secondary hover:border-cos-teal/25'
                      }`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            )}
            {!msgsFresh ? (
              <div className="px-3">
                <Pulse rows={5} />
              </div>
            ) : msgs.error && msgRows.length === 0 ? (
              <EmptyBlock icon="cloud_off">{msgs.error}</EmptyBlock>
            ) : (
              <DataTable columns={recipientColumns} rows={msgRows} rowKey={(m) => m.id} emptyLabel="No recipients." />
            )}
            {msgsFresh && msgRows.length > 0 && messagesPager}
          </div>
        )}

        {/* Analytics */}
        {tab === 'analytics' &&
          (detail.loading && !detailRow ? (
            <Pulse rows={4} />
          ) : !detailRow ? (
            <EmptyBlock icon="cloud_off">Analytics unavailable — retry loading the campaign.</EmptyBlock>
          ) : (
            <div className="space-y-4">
              {channels.length > 0 && (
                <div className="glass-inset p-4">
                  <div className="mb-0.5 flex items-baseline gap-2">
                    <div className="text-[13px] font-semibold text-cos-ink">{hasCopies ? 'Reach by address field' : 'Reach'}</div>
                    <span className="flex-1" />
                    <span className="shrink-0 text-2xs text-cos-ink-muted">
                      <span className="mono font-bold text-cos-ink">{num(reach)}</span> addresses in all
                    </span>
                  </div>
                  <p className="mb-3.5 text-2xs leading-[15px] text-cos-ink-muted">
                    {hasCopies
                      ? 'One mail, several address fields, each tracked on its own. The rings share one scale, so they answer which field engaged best; the bar beneath them answers how the send was split.'
                      : 'Every vendor addressed in the To line, each mail tracked on its own: how many were sent, delivered and opened.'}
                  </p>

                  <ChannelReach
                    channels={channels}
                    onSelect={(kind) => {
                      setTab('recipients');
                      setKind(kind);
                      setPage(1);
                    }}
                  />
                </div>
              )}

              <div className="glass-inset p-4">
                <div className="mb-2 text-[13px] font-semibold text-cos-ink">Engagement — last 30 days</div>
                {daily.length === 0 ? (
                  <p className="py-6 text-center text-2xs text-cos-ink-muted">No engagement events yet.</p>
                ) : (
                  <LineAreaChart
                    data={daily}
                    xKey="label"
                    height={190}
                    series={[
                      { key: 'delivered', name: 'Delivered', color: chart.bronze, area: true },
                      { key: 'opened', name: 'Opened', color: chart.teal, area: true },
                      { key: 'clicked', name: 'Clicked', color: chart.tealDeep },
                    ]}
                  />
                )}
              </div>

              <div className="glass-inset p-4">
                <div className="mb-2 text-[13px] font-semibold text-cos-ink">Delivery breakdown</div>
                {donut.length === 0 ? (
                  <p className="py-6 text-center text-2xs text-cos-ink-muted">No recipients yet.</p>
                ) : (
                  <DonutChart
                    data={donut}
                    colorFor={(n) => BREAKDOWN_COLOR[n]}
                    centerValue={num(counts.total_count)}
                    centerLabel="Recipients"
                    height={168}
                  />
                )}
              </div>

              <div className="glass-inset p-4">
                <div className="mb-1 text-[13px] font-semibold text-cos-ink">Recent events</div>
                {events.length === 0 ? (
                  <p className="py-4 text-center text-2xs text-cos-ink-muted">No events recorded yet.</p>
                ) : (
                  <ul>
                    {events.map((e) => {
                      const p = messagePill(String(e.event_type || '').toUpperCase());
                      return (
                        <li key={e.id} className="flex items-center justify-between gap-3 border-b border-cos-line-subtle py-2 last:border-0">
                          <div className="flex min-w-0 items-center gap-2">
                            <StatusPill status={p.pill} label={p.label} />
                            <span className="mono truncate text-2xs text-cos-ink-secondary">{e.recipient}</span>
                            {hasCopies && <ChannelChip kind={e.kind} />}
                          </div>
                          <span className="mono shrink-0 text-2xs text-cos-ink-muted">{when(e.occurred_at)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          ))}

        {/* Failed to deliver: why each address did not get it, the Excel per reason, the email-field
            fixer and the retry — this campaign only. */}
        {tab === 'failed' && <CampaignAdvanced campaignId={rawId} canResend={canResend} onResent={refreshAll} onRetry={() => setResendOpen(true)} />}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-cos-line-subtle px-6 py-3">
        <span
          className="flex min-w-0 items-center gap-1.5 text-2xs text-cos-ink-muted"
          title={
            trackingLive
              ? 'Delivery, open and click events are pushed by the mail provider as they happen'
              : 'No provider webhook is configured, so no delivery or open events can reach this server'
          }
        >
          {!trackingLive && <Icon name="warning" className="text-[14px] text-cos-amber" />}
          <span className="truncate">{footerNote}</span>
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button variant="ghost" size="sm" icon="refresh" disabled={detail.loading} onClick={refreshAll}>
            Refresh
          </Button>
        </div>
      </div>

    </Drawer>
  );
}

export default CampaignDetail;
